/**
 * Pure response classifier for the global SSE base layer.
 *
 * Given an HTTP `Response` (or a thrown fetch error), returns a `ResponseAction`
 * telling the connection runner what to do next. Contains NO I/O, NO network
 * calls, and NO Vue dependencies — safe to unit-test in isolation.
 */

/**
 * Discriminated union describing what the connection runner should do after
 * a (re)connect attempt.
 */
export type ResponseAction =
  | { kind: 'stream' } // 200 + text/event-stream: open and consume
  | { kind: 'complete' } // 204: server says the stream is done (terminal 'completed')
  | { kind: 'refresh' } // 401: refresh the token once, then retry
  | { kind: 'fatal' } // non-retryable (terminal 'forbidden')
  | { kind: 'retry'; retryAfterMs?: number }; // transient: reconnect (optionally after server-hinted delay)

/**
 * Parse an HTTP `Retry-After` header value (delta-seconds OR an HTTP date)
 * into milliseconds, or `undefined` if the value is absent / unparseable.
 *
 * @param headerValue - Raw value from `response.headers.get('retry-after')`, or `null`.
 * @param nowMs - Current epoch in ms (pass `Date.now()` at the call site so the function stays pure).
 */
export function parseRetryAfterMs(
  headerValue: string | null,
  nowMs: number
): number | undefined {
  if (headerValue === null || headerValue.trim() === '') {
    return undefined;
  }

  const trimmed = headerValue.trim();

  // Delta-seconds: one or more ASCII digits only.
  if (/^\d+$/.test(trimmed)) {
    return parseInt(trimmed, 10) * 1000;
  }

  // HTTP-date: attempt Date.parse.
  const parsed = Date.parse(trimmed);
  if (isNaN(parsed)) {
    return undefined;
  }

  return Math.max(0, parsed - nowMs);
}

/**
 * Classify an HTTP response from a stream (re)connect attempt.
 *
 * Rules (in evaluation order):
 * - 204 → `complete`
 * - 200 + `text/event-stream` content-type → `stream`
 * - 200 without `text/event-stream` → `fatal` (misconfigured endpoint / proxy login page)
 * - 401 → `refresh`
 * - 408 → `retry`
 * - 429 → `retry` (with optional `Retry-After` hint)
 * - 500–599 → `retry` (with optional `Retry-After` hint)
 * - any other 4xx → `fatal`
 * - anything else → `fatal` (safe default)
 */
export function classifyResponse(response: Response): ResponseAction {
  const { status } = response;

  if (status === 204) {
    return { kind: 'complete' };
  }

  if (status === 200) {
    const contentType = response.headers.get('content-type') ?? '';
    if (contentType.includes('text/event-stream')) {
      return { kind: 'stream' };
    }
    return { kind: 'fatal' };
  }

  if (status === 401) {
    return { kind: 'refresh' };
  }

  if (status === 408) {
    return { kind: 'retry' };
  }

  if (status === 429) {
    const retryAfterMs = parseRetryAfterMs(
      response.headers.get('retry-after'),
      Date.now()
    );
    return { kind: 'retry', retryAfterMs };
  }

  if (status >= 500 && status <= 599) {
    const retryAfterMs = parseRetryAfterMs(
      response.headers.get('retry-after'),
      Date.now()
    );
    return { kind: 'retry', retryAfterMs };
  }

  if (status >= 400 && status <= 499) {
    return { kind: 'fatal' };
  }

  return { kind: 'fatal' };
}

/**
 * Classify a thrown fetch error.
 *
 * The runner handles `AbortError` before reaching this function, so every
 * error here is treated as a transient network failure worth retrying.
 *
 * @param error - The thrown value (typically a `TypeError` for network failures).
 */
export function classifyError(error: unknown): ResponseAction {
  // Every non-abort error reaching here is a transient network failure; the
  // argument is part of the contract (callers pass the caught error) even
  // though the current policy does not branch on it.
  void error;
  return { kind: 'retry' };
}
